import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { FilterMatchMode } from 'primereact/api';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import {
  DataTable,
  DataTableFilterMetaData,
  DataTableSortMeta,
  DataTableStateEvent
} from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { selectedDate, Task } from 'types/interfaces';
import { TaskAssignmentResult } from 'types/interfaces/TaskAssignmentResult';
import { TreeNode } from 'types/interfaces/TreeNode';
import { formatDateStringFromRaw } from 'utils/date-utils';
import { normalizeApiError } from 'utils/taskCard/taskAssignmentErrors';

import { GlobalSettings } from 'components/services/GlobalSettingsService';
import { TaskService } from 'components/services/TaskService';
import Datepicker from 'ui/custom-controls/Datepicker';
import { withDialogFooterSpacing } from 'ui/shared/dialogFooterSpacing';

import AutoDepartmentAssignmentDialog from './AutoDepartmentAssignmentDialog';
import { DepartmentCell } from './DepartmentCell';
import { DepartmentProvider } from './DepartmentContext';

const columnWidths = {
  id: { minWidth: 100, width: '12%' },
  taskType: { minWidth: 100, width: '12%' },
  name: { minWidth: 100, width: '12%' },
  taskStatus: { minWidth: 100, width: '12%' },
  assetName: { minWidth: 100, width: '12%' },
  creationDate: { minWidth: 100, width: '12%' },
  plannedStartDate: { minWidth: 100, width: '12%' },
  plannedEndDate: { minWidth: 100, width: '12%' },
  checkbox: { minWidth: 20, width: '4%' },
  department: { width: 430 }
};

interface DepartmentAssignmentDialogProps {
  openDepartmentAssignmentDialog: boolean;
  setOpenDepartmentAssignmentDialog: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  departments: TreeNode[];
  globalSettings: GlobalSettings;
}

function safeFormatDate(raw?: string | null): string {
  if (!raw) return '';
  try {
    return formatDateStringFromRaw(raw);
  } catch {
    return '';
  }
}

const DepartmentAssignmentDialog: React.FC<DepartmentAssignmentDialogProps> = ({
  openDepartmentAssignmentDialog,
  setOpenDepartmentAssignmentDialog,
  departments,
  globalSettings
}) => {
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState<any[]>([]);
  const [showAllDepartments, setShowAllDepartments] = useState(false);
  const [nextRunDateTime, setNextRunDateTime] = useState<Date | null>(null);
  const [selectedDepartments, setSelectedDepartments] = useState<
    Record<number, TreeNode | null>
  >({});
  const [selectedTasks, setSelectedTasks] = useState<Record<number, boolean>>(
    {}
  );
  const [
    openAutoDepartmentAssignmentDialog,
    setOpenAutoDepartmentAssignmentDialog
  ] = useState<boolean>(false);
  const [selectedTasksToAssign, setSelectedTasksToAssign] = useState<number[]>(
    []
  );
  const [tasksToAssignResponse, setTasksToAssignResponse] = useState<
    TaskAssignmentResult[] | []
  >([]);
  const [selectedDateFilter, setSelectedDateFilter] = useState<selectedDate>(
    () => {
      const saved = localStorage.getItem('selectedDateFilter');
      return saved
        ? JSON.parse(saved)
        : {
            dateFrom: '',
            dateUntil: ''
          };
    }
  );

  const prevParamsRef = useRef<string>('');
  const [filtersRestored, setFiltersRestored] = useState(false);
  const isFirstRender = useRef(true);

  const handleDateFilterChange = useCallback(
    (selection: selectedDate) => {
      if (isFirstRender.current) {
        isFirstRender.current = false;
        return;
      }

      const isDateFromValid =
        selection.dateFrom !== null &&
        !isNaN(new Date(selection.dateFrom)?.getTime());
      const isDateUntilValid =
        selection.dateUntil !== null &&
        !isNaN(new Date(selection.dateUntil)?.getTime());

      if (isDateFromValid || isDateUntilValid) {
        const newDateFrom = isDateFromValid
          ? String(new Date(selection.dateFrom!)?.getTime())
          : '';
        const newDateUntil = isDateUntilValid
          ? String(new Date(selection.dateUntil!)?.getTime())
          : '';

        if (
          newDateFrom !== selectedDateFilter.dateFrom ||
          newDateUntil !== selectedDateFilter.dateUntil
        ) {
          setSelectedDateFilter({
            dateFrom: newDateFrom,
            dateUntil: newDateUntil
          });
        }
      } else {
        setSelectedDateFilter({ dateFrom: '', dateUntil: '' });
      }
    },
    [selectedDateFilter.dateFrom, selectedDateFilter.dateUntil]
  );

  const toggleShowAllDepartments = useCallback(() => {
    setShowAllDepartments((prev) => !prev);
  }, []);

  const [filters, setFilters] = useState<
    Record<string, DataTableFilterMetaData>
  >({
    id: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    taskType: { value: null, matchMode: FilterMatchMode.CONTAINS },
    taskStatus: { value: null, matchMode: FilterMatchMode.CONTAINS },
    assetName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    creationDate: { value: null, matchMode: FilterMatchMode.CONTAINS },
    plannedStartDate: { value: null, matchMode: FilterMatchMode.CONTAINS },
    plannedEndDate: { value: null, matchMode: FilterMatchMode.CONTAINS }
  });

  const [multiSortMeta, setMultiSortMeta] = useState<
    DataTableSortMeta[] | undefined | null
  >(undefined);

  const currentParams = useMemo(() => {
    return JSON.stringify({
      filters,
      multiSortMeta,
      selectedDateFilter
    });
  }, [filters, multiSortMeta, selectedDateFilter]);

  const setMultiSortMetaCallback = useCallback((e: DataTableStateEvent) => {
    setMultiSortMeta(e.multiSortMeta ?? null);
  }, []);

  const loadTasks = useCallback(async () => {
    if (!openDepartmentAssignmentDialog) return;
    const sort = multiSortMeta?.[0];

    const payload = JSON.stringify([
      {
        field: 'id',
        value: filters.id?.value,
        operator: filters.id?.matchMode
      },
      {
        field: 'name',
        value: filters.name?.value,
        operator: filters.name?.matchMode
      },
      {
        field: 'taskType',
        value: filters.taskType?.value,
        operator: filters.taskType?.matchMode
      },
      {
        field: 'taskStatus',
        value: filters.taskStatus?.value,
        operator: filters.taskStatus?.matchMode
      },
      {
        field: 'assetName',
        value: filters.assetName?.value,
        operator: filters.assetName?.matchMode
      },
      {
        field: 'creationDate',
        value: filters.creationDate?.value,
        operator: filters.creationDate?.matchMode
      },
      {
        field: 'plannedStartDate',
        value: filters.plannedStartDate?.value,
        operator: filters.plannedStartDate?.matchMode
      },
      {
        field: 'plannedEndDate',
        value: filters.plannedEndDate?.value,
        operator: filters.plannedEndDate?.matchMode
      },
      {
        field: 'creationDateBetween',
        value: selectedDateFilter.dateFrom
          ? new Date(parseInt(selectedDateFilter.dateFrom)).toISOString()
          : '',
        additionalValue: selectedDateFilter.dateUntil
          ? new Date(parseInt(selectedDateFilter.dateUntil)).toISOString()
          : '',
        operator: FilterMatchMode.BETWEEN
      }
    ]);

    setLoading(true);
    try {
      const nextRunResponse =
        await TaskService.getDepartmentAssignmentSchedule();
      if (nextRunResponse.data.isSuccess && nextRunResponse.data.value) {
        setNextRunDateTime(new Date(nextRunResponse.data.value));
      }

      const resp = await TaskService.getDepartmentAssignment({
        sortBy: sort?.field,
        sortDirection: sort?.order === 1 ? 'asc' : 'desc',
        filters: payload
      });

      const tasksData = resp.data?.value;
      setTasks(Array.isArray(tasksData) ? tasksData : []);
      setSelectedTasks({});
      setSelectedDepartments({});
    } catch (error) {
      console.error('Error loading tasks:', error);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [
    openDepartmentAssignmentDialog,
    filters,
    multiSortMeta,
    selectedDateFilter
  ]);

  useEffect(() => {
    if (!openDepartmentAssignmentDialog) {
      // Компонент остаётся смонтированным при закрытии диалога, поэтому
      // сбрасываем guard вручную: иначе повторное открытие с теми же
      // фильтрами не перезагрузит данные.
      prevParamsRef.current = '';
      return;
    }
    if (!filtersRestored) return;
    if (prevParamsRef.current === currentParams) {
      return;
    }
    prevParamsRef.current = currentParams;
    loadTasks();
  }, [
    openDepartmentAssignmentDialog,
    filtersRestored,
    currentParams,
    loadTasks
  ]);

  useEffect(() => {
    if (openDepartmentAssignmentDialog) {
      setSelectedDepartments({});
      setSelectedTasks({});
      const saved = localStorage.getItem('selectedDateFilter');
      setSelectedDateFilter(
        saved
          ? JSON.parse(saved)
          : {
              dateFrom: '',
              dateUntil: ''
            }
      );
      setFiltersRestored(true);
    } else {
      setFiltersRestored(false);
    }
  }, [openDepartmentAssignmentDialog]);

  const handleSelectAll = useCallback(() => {
    const allSelected =
      tasks.length > 0 && tasks.every((task) => selectedTasks[task.id]);
    setSelectedTasks(
      allSelected
        ? {}
        : Object.fromEntries(tasks.map((task) => [task.id, true]))
    );
  }, [tasks, selectedTasks]);

  const handleAssignSelected = useCallback(async () => {
    const selectedTaskIds = Object.keys(selectedTasks)
      .filter((taskId) => selectedTasks[Number(taskId)] === true)
      .map(Number);

    if (selectedTaskIds.length === 0) return;

    setLoading(true);

    try {
      const response = await TaskService.AutoTaskAssignment(
        selectedTaskIds,
        true
      );

      setSelectedTasksToAssign(selectedTaskIds);
      if (response.status === 400) {
        const error = normalizeApiError(response.data);
        setTasksToAssignResponse(
          error
            ? selectedTaskIds.map((taskId) => ({ taskId, errors: [error] }))
            : []
        );
      } else {
        setTasksToAssignResponse(
          response.data.value as unknown as TaskAssignmentResult[]
        );
      }

      setOpenAutoDepartmentAssignmentDialog(true);
    } catch (error) {
      console.error('Error during auto assignment:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedTasks]);

  const onFiltersChange = useCallback((e: DataTableStateEvent) => {
    const next = e.filters as Record<string, DataTableFilterMetaData>;
    setFilters(next);
  }, []);

  const departmentContextValue = useMemo(
    () => ({
      showAllDepartments,
      toggleShowAllDepartments,
      departments,
      loadTasks,
      selectedDepartments,
      setSelectedDepartments
    }),
    [
      showAllDepartments,
      toggleShowAllDepartments,
      departments,
      loadTasks,
      selectedDepartments
    ]
  );

  return (
    <DepartmentProvider value={departmentContextValue}>
      <>
        <AutoDepartmentAssignmentDialog
          openAutoDepartmentAssignmentDialog={
            openAutoDepartmentAssignmentDialog
          }
          setOpenAutoDepartmentAssignmentDialog={
            setOpenAutoDepartmentAssignmentDialog
          }
          selectedTasksToAssign={selectedTasksToAssign}
          tasksToAssignResponse={tasksToAssignResponse}
          loadTasks={loadTasks}
        />

        <Dialog
          header="Распределение нарядов по участкам"
          visible={openDepartmentAssignmentDialog}
          style={{
            width: '95vw',
            maxWidth: 2200,
            minWidth: 800,
            maxHeight: '92vh',
            minHeight: 300
          }}
          contentStyle={{
            height: 'calc(92vh - 4rem)',
            overflow: 'auto'
          }}
          onHide={() => setOpenDepartmentAssignmentDialog(false)}
          pt={{
            ...withDialogFooterSpacing(null, {
              department: true,
              footerPaddingTop: '10px'
            }),
            content: {
              style: {
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                padding: '1rem'
              }
            }
          }}
          footer={
            <div
              className="flex justify-content-between align-items-center"
              style={{ width: '100%' }}
            >
              <div className="flex gap-2">
                <Button
                  label="Выбрать все"
                  icon="pi pi-check-square"
                  onClick={handleSelectAll}
                  severity="secondary"
                  disabled={loading}
                />
                <Button
                  label="Распределить выбранные"
                  icon="pi pi-share-alt"
                  onClick={handleAssignSelected}
                  disabled={
                    !Object.values(selectedTasks).some((v) => v) || loading
                  }
                  severity={
                    !Object.values(selectedTasks).some((v) => v)
                      ? 'secondary'
                      : undefined
                  }
                />
              </div>
              <div>
                <Button
                  style={{ width: 40 }}
                  icon="pi pi-chart-line"
                  onClick={() => window.open('/departmentAssignmentHistory')}
                  tooltip="Журнал автоматических распределений"
                  tooltipOptions={{ position: 'top' }}
                  disabled={loading}
                />
                <Button
                  label="Закрыть"
                  icon="pi pi-times"
                  onClick={() => setOpenDepartmentAssignmentDialog(false)}
                  severity="secondary"
                />
              </div>
            </div>
          }
        >
          <Datepicker
            datepickerDate={selectedDateFilter as selectedDate}
            onChange={handleDateFilterChange}
          />
          {nextRunDateTime && (
            <div className="next-assignment-info mt-2 mb-2">
              Следующее распределение согласно расписанию произойдет{' '}
              <strong>
                {nextRunDateTime.toLocaleDateString('ru-RU', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </strong>{' '}
              в{' '}
              <strong>
                {nextRunDateTime.toLocaleTimeString('ru-RU', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </strong>
            </div>
          )}
          <div
            style={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              overflow: 'hidden'
            }}
          >
            {loading && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  zIndex: 1000,
                  borderRadius: 'inherit'
                }}
              >
                <ProgressSpinner />
              </div>
            )}
            <DataTable
              size="small"
              value={tasks}
              lazy
              multiSortMeta={multiSortMeta}
              onSort={setMultiSortMetaCallback}
              sortMode="multiple"
              removableSort
              filters={filters}
              onFilter={onFiltersChange}
              scrollable
              scrollHeight="100%"
              selectionMode="checkbox"
              selection={tasks.filter((task) => selectedTasks[task.id])}
              onSelectionChange={(e) => {
                const newSelection: Record<number, boolean> = {};
                e.value.forEach((task: any) => {
                  newSelection[task.id] = true;
                });
                setSelectedTasks(newSelection);
              }}
              style={{ height: '100%' }}
            >
              <Column
                selectionMode="multiple"
                style={{ width: columnWidths.checkbox.width }}
                headerStyle={{ width: columnWidths.checkbox.width }}
              />

              <Column
                field="id"
                header="№"
                filter
                sortable
                filterPlaceholder="Поиск по №"
                style={{ width: columnWidths.id.width }}
              />

              <Column
                field="taskType"
                header="Тип"
                filter
                sortable
                filterPlaceholder="Поиск по типу"
                style={{ width: columnWidths.taskType.width }}
              />

              <Column
                field="name"
                header="Название"
                filter
                sortable
                filterPlaceholder="Поиск по названию"
                style={{ width: columnWidths.name.width }}
              />

              <Column
                field="taskStatus"
                header="Статус"
                filter
                sortable
                filterPlaceholder="Поиск по статусу"
                style={{ width: columnWidths.taskStatus.width }}
              />

              <Column
                field="assetName"
                header="Объект"
                filter
                sortable
                filterPlaceholder="Поиск по объекту"
                style={{ width: columnWidths.assetName.width }}
              />

              <Column
                field="creationDate"
                header="Время регистрации"
                filter
                sortable
                filterPlaceholder="Поиск по дате"
                style={{ width: columnWidths.creationDate.width }}
                body={(r: Task) =>
                  safeFormatDate(
                    (r as any).creationDate || (r as any).factFinish
                  )
                }
              />

              <Column
                field="plannedStartDate"
                header="Плановое начало"
                filter
                sortable
                filterPlaceholder="Поиск по дате"
                style={{ width: columnWidths.plannedStartDate.width }}
                body={(r: Task) =>
                  safeFormatDate(
                    (r as any).plannedStartDate || (r as any).factFinish
                  )
                }
              />

              <Column
                field="plannedEndDate"
                header="Плановое завершение"
                filter
                sortable
                filterPlaceholder="Поиск по дате"
                style={{ width: columnWidths.plannedEndDate.width }}
                body={(r: Task) =>
                  safeFormatDate(
                    (r as any).plannedEndDate || (r as any).factFinish
                  )
                }
              />

              <Column
                field="Departament"
                style={{ width: columnWidths.department.width }}
                body={(rowData) => (
                  <DepartmentCell
                    task={rowData}
                    globalSettings={globalSettings}
                  />
                )}
              />
            </DataTable>
          </div>
        </Dialog>
      </>
    </DepartmentProvider>
  );
};

export default DepartmentAssignmentDialog;
